// src/services/SubscriptionService.js
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { isPaymentEnforcementEnabled } from '../utils/RemoteConfig';


/**
 * Service for managing subscription status and access control
 */
const SubscriptionService = {
  
  /**
   * Check if user has access to the app
   * Returns comprehensive subscription status
   * 
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} Status object with:
   *   - hasAccess: boolean - Can user access the app?
   *   - status: string - 'beta' | 'trial' | 'active' | 'paused' | 'canceled' | 'none'
   *   - daysLeftInTrial: number | null - Days remaining if on trial
   *   - subscription: Object | null - Full subscription data
   *   - reason: string - Why they have/don't have access
   */
  async checkAccess(userId) {
    console.log('SubscriptionService - Checking access for user:', userId);
    
    // Check if payment enforcement is enabled via Remote Config
    const enforcementEnabled = isPaymentEnforcementEnabled();
    
    if (!enforcementEnabled) {
      console.log('SubscriptionService - Payment enforcement disabled (beta mode)');
      return {
        hasAccess: true,
        status: 'beta',
        daysLeftInTrial: null,
        subscription: null,
        reason: 'Beta testing mode - all users have access'
      };
    }
    
    // Payment enforcement is enabled - check subscription
    try {
      const subscriptionData = await this.getActiveSubscription(userId);
      
      if (!subscriptionData) {
        console.log('SubscriptionService - No active subscription found');
        return {
          hasAccess: false,
          status: 'none',
          daysLeftInTrial: null,
          subscription: null,
          reason: 'No active subscription or trial'
        };
      }
      
      const { subscription, daysLeftInTrial } = subscriptionData;
      const status = subscription.status;
      
      // Determine access based on subscription status
      const hasAccess = ['trialing', 'active'].includes(status);
      
      let reason = '';
      if (status === 'trialing') {
        reason = `Free trial - ${daysLeftInTrial} days remaining`;
      } else if (status === 'active') {
        reason = 'Active subscription';
      } else if (status === 'paused') {
        reason = 'Subscription paused - payment method required';
      } else if (status === 'canceled') {
        reason = 'Subscription canceled';
      } else if (status === 'past_due') {
        reason = 'Payment failed - please update payment method';
      } else {
        reason = 'Subscription inactive';
      }
      
      console.log('SubscriptionService - Access check result:', {
        hasAccess,
        status,
        daysLeftInTrial,
        reason
      });
      
      return {
        hasAccess,
        status,
        daysLeftInTrial,
        subscription,
        reason
      };
      
    } catch (error) {
      console.error('SubscriptionService - Error checking access:', error);
      // Fail open - grant access on error to prevent locking out users
      return {
        hasAccess: true,
        status: 'error',
        daysLeftInTrial: null,
        subscription: null,
        reason: 'Error checking subscription - access granted'
      };
    }
  },
  
  /**
   * Get active subscription for user
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object|null>} Subscription data with daysLeftInTrial
   */
  async getActiveSubscription(userId) {
    try {
      const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');
      const q = query(
        subscriptionsRef,
        where('status', 'in', ['trialing', 'active', 'past_due', 'paused'])
      );
      
      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            unsubscribe(); // Unsubscribe after first result
            
            if (snapshot.empty) {
              resolve(null);
              return;
            }
            
            // Get the first (should be only) subscription
            const doc = snapshot.docs[0];
            const subscription = { id: doc.id, ...doc.data() };
            
            // Calculate trial days remaining if on trial
            let daysLeftInTrial = null;
            if (subscription.status === 'trialing' && subscription.trial_end) {
              const trialEnd = subscription.trial_end.toDate();
              const now = new Date();
              const diffTime = trialEnd - now;
              daysLeftInTrial = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              daysLeftInTrial = Math.max(0, daysLeftInTrial); // Don't show negative days
            }
            
            resolve({ subscription, daysLeftInTrial });
          },
          (error) => {
            console.error('SubscriptionService - Error fetching subscription:', error);
            reject(error);
          }
        );
      });
      
    } catch (error) {
      console.error('SubscriptionService - Error in getActiveSubscription:', error);
      return null;
    }
  },
  
  /**
   * Subscribe to real-time subscription updates
   * @param {string} userId - Firebase user ID
   * @param {Function} callback - Called with subscription data on updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToSubscription(userId, callback) {
    console.log('SubscriptionService - Setting up real-time subscription listener');
    
    const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');
    const q = query(
      subscriptionsRef,
      where('status', 'in', ['trialing', 'active', 'past_due', 'paused', 'canceled'])
    );
    
    return onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          callback(null);
          return;
        }
        
        const doc = snapshot.docs[0];
        const subscription = { id: doc.id, ...doc.data() };
        
        // Calculate trial days remaining
        let daysLeftInTrial = null;
        if (subscription.status === 'trialing' && subscription.trial_end) {
          const trialEnd = subscription.trial_end.toDate();
          const now = new Date();
          const diffTime = trialEnd - now;
          daysLeftInTrial = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          daysLeftInTrial = Math.max(0, daysLeftInTrial);
        }
        
        callback({ subscription, daysLeftInTrial });
      },
      (error) => {
        console.error('SubscriptionService - Subscription listener error:', error);
        callback(null);
      }
    );
  },
  
  /**
   * Get available pricing plans from Firestore
   * @returns {Promise<Array>} Array of products with prices
   */
  async getAvailablePlans() {
    try {
      const productsRef = collection(db, 'products');
      const productsSnapshot = await getDocs(query(productsRef, where('active', '==', true)));
      
      const plans = [];
      
      for (const productDoc of productsSnapshot.docs) {
        const product = { id: productDoc.id, ...productDoc.data() };
        
        // Get prices for this product
        const pricesRef = collection(db, 'products', productDoc.id, 'prices');
        const pricesSnapshot = await getDocs(query(pricesRef, where('active', '==', true)));
        
        const prices = pricesSnapshot.docs.map(priceDoc => ({
          id: priceDoc.id,
          ...priceDoc.data()
        }));
        
        plans.push({
          ...product,
          prices
        });
      }
      
      console.log('SubscriptionService - Available plans:', plans);
      return plans;
      
    } catch (error) {
      console.error('SubscriptionService - Error fetching plans:', error);
      return [];
    }
  },
  
  /**
   * Simple helper functions for common checks
   */
  
  isOnTrial(subscriptionData) {
    return subscriptionData?.subscription?.status === 'trialing';
  },
  
  isActive(subscriptionData) {
    return subscriptionData?.subscription?.status === 'active';
  },
  
  isPaused(subscriptionData) {
    return subscriptionData?.subscription?.status === 'paused';
  },
  
  isCanceled(subscriptionData) {
    return subscriptionData?.subscription?.status === 'canceled';
  },
  
  needsPaymentMethod(subscriptionData) {
    return ['paused', 'past_due'].includes(subscriptionData?.subscription?.status);
  }
};

export default SubscriptionService;